import { Controller, type Control, type FieldErrors, type UseFormRegister, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import { Grid, TextField, MenuItem, Autocomplete, CircularProgress } from '@mui/material';
import { useProvincias, useLocalidades } from '../../../hooks/useGeografia';
import type { AfiliadoFormValues } from '../afiliadoSchema';

interface Props {
  control: Control<AfiliadoFormValues>;
  register: UseFormRegister<AfiliadoFormValues>;
  errors: FieldErrors<AfiliadoFormValues>;
  watch: UseFormWatch<AfiliadoFormValues>;
  setValue: UseFormSetValue<AfiliadoFormValues>;
}

export default function TabDomicilio({ control, register, errors, watch, setValue }: Props) {
  const provinciaId = watch('provincia_id');

  const { data: provincias = [], isLoading: loadingProvincias } = useProvincias();
  const { data: localidades = [], isLoading: loadingLocalidades } = useLocalidades(
    provinciaId ?? undefined
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          {...register('domicilio')}
          fullWidth
          label="Domicilio"
          placeholder="Calle, numero, piso, depto"
          error={!!errors.domicilio}
          helperText={errors.domicilio?.message}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="provincia_id"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value || null);
                // Al cambiar de provincia, limpiar localidad
                setValue('localidad_id', null);
              }}
              select
              fullWidth
              label="Provincia"
              disabled={loadingProvincias}
              InputProps={{
                endAdornment: loadingProvincias ? <CircularProgress size={18} /> : null,
              }}
            >
              <MenuItem value="">
                <em>Sin especificar</em>
              </MenuItem>
              {provincias.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nombre}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="localidad_id"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={localidades.find((l) => l.id === field.value) ?? null}
              onChange={(_, opt) => field.onChange(opt?.id ?? null)}
              options={localidades}
              getOptionLabel={(opt) => opt.nombre}
              disabled={!provinciaId || loadingLocalidades}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Localidad"
                  placeholder={provinciaId ? 'Buscar...' : 'Seleccione una provincia primero'}
                />
              )}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          {...register('telefono')}
          fullWidth
          label="Telefono"
          error={!!errors.telefono}
          helperText={errors.telefono?.message}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          {...register('email')}
          type="email"
          fullWidth
          label="Email"
          error={!!errors.email}
          helperText={errors.email?.message}
        />
      </Grid>
    </Grid>
  );
}
